import React, { ReactNode, createContext, useContext, useMemo, useState } from 'react';

import { type DateTime } from 'luxon';

import { type IStudentGradesModel } from '@common/models/StudentGrades/StudentGrades';
import { type IStudentSimpleProgramModel } from '@common/models/StudentPrograms/StudentSimpleProgram';

import useGetMonthGrades from '../hooks/useGetMonthGrades';

interface MonthCalendarContextType {
	isCalendarView: boolean;
	isIncludeWeekDays: boolean;
	setIsCalendarView: (view: boolean) => void;
	setIsIncludeWeekDays: (view: boolean) => void;
	dateTime: DateTime<boolean>;
	setDateTime: (newDateTime: DateTime) => void;
	selectedPrograms: IStudentSimpleProgramModel[];
	onProgramChange: (value: IStudentSimpleProgramModel[]) => void;
	studentGrades: IStudentGradesModel;
}

const MonthCalendarContext = createContext<MonthCalendarContextType | undefined>(undefined);

export function MonthCalendarProvider({ children }: { children: ReactNode }) {
	const [isCalendarView, setIsCalendarView] = useState(false);
	const [isIncludeWeekDays, setIsIncludeWeekDays] = useState(false);

	const { selectedPrograms, setDateTime, studentGrades, onProgramChange, dateTime } =
		useGetMonthGrades(isCalendarView);

	const contextValue = useMemo(
		() => ({
			isCalendarView,
			setIsCalendarView,
			isIncludeWeekDays,
			setIsIncludeWeekDays,
			selectedPrograms,
			setDateTime,
			onProgramChange,
			dateTime,
			studentGrades,
		}),
		[
			isCalendarView,
			isIncludeWeekDays,
			selectedPrograms,
			setDateTime,
			onProgramChange,
			dateTime,
			studentGrades,
		],
	);

	return (
		<MonthCalendarContext.Provider value={contextValue}>{children}</MonthCalendarContext.Provider>
	);
}

export const useCalendarContext = (): MonthCalendarContextType => {
	const context = useContext(MonthCalendarContext);
	if (!context) {
		throw new Error('useCalendarView must be used within a CalendarViewProvider');
	}
	return context;
};
